import { useCallback, useState } from 'react';
import { useUserBalance } from '../../../entities/user/hooks/useUserBalance';
import { AuthUser } from '../../../services/authService';
import {
  ChartData,
  createInitialGameState,
  GameEndData,
  GameState,
  INITIAL_BALANCE,
  MAX_CANDLE_COUNT,
  Trade,
} from '../../../shared/types/game.types';
import { calculateStockValue } from '../../../shared/utils/tradingFees';
import { generateNewCandle } from '../utils/candleGenerator';

interface UseGameSessionProps {
  user: AuthUser | null;
  accessToken: string | null;
  apiBaseUrl: string;
  refreshAccessToken: () => Promise<string | null>;
  updateUser: (user: AuthUser) => void;
  onGameStart: (backgroundData: ChartData[], firstCandle: ChartData) => void;
  onGameEnd: (gameEndData: GameEndData) => void;
  getTradeHistory: () => Trade[];
}

/**
 * 게임 세션 관리 훅
 */
export const useGameSession = ({
  user,
  accessToken,
  apiBaseUrl,
  refreshAccessToken,
  updateUser,
  onGameStart,
  onGameEnd,
  getTradeHistory,
}: UseGameSessionProps) => {
  const [gameState, setGameState] = useState<GameState>(
    createInitialGameState(user?.balance ?? INITIAL_BALANCE)
  );
  const [timeLeft, setTimeLeft] = useState(300);
  const [isAutoMode, setIsAutoMode] = useState(true);
  const [showGameEndPopup, setShowGameEndPopup] = useState(false);
  const [showExitConfirmPopup, setShowExitConfirmPopup] = useState(false);
  const [gameEndData, setGameEndData] = useState<GameEndData | null>(null);

  const { persistBalance } = useUserBalance({
    user,
    accessToken,
    apiBaseUrl,
    refreshAccessToken,
    updateUser,
  });

  const resetGameState = useCallback((balance: number) => {
    setGameState(createInitialGameState(balance));
    setTimeLeft(300);
    setShowGameEndPopup(false);
    setShowExitConfirmPopup(false);
    setGameEndData(null);
  }, []);

  const endGame = useCallback(async () => {
    const finalBalance = gameState.playerBalance;
    const finalStockQuantity = gameState.stockQuantity;
    const finalCurrentPrice = gameState.currentPrice;
    const finalCandleCount = gameState.candleCount;
    const initialBalanceBeforeReset = gameState.initialBalance;

    // 게임 종료 시점의 마지막 캔들 가져오기
    const lastCandle =
      gameState.history.length > 0
        ? gameState.history[gameState.history.length - 1]
        : undefined;

    // 주식 평가액은 매도 수수료와 거래세를 차감한 금액
    const stockValue = calculateStockValue(
      finalCurrentPrice,
      finalStockQuantity
    );
    const totalAssets = finalBalance + stockValue;
    const normalizedTotalAssets = Math.max(Math.floor(totalAssets), 0);
    const targetBalance = user
      ? normalizedTotalAssets
      : initialBalanceBeforeReset;

    // 게임 종료 시 전량 청산(강제 매도) Trade를 기록 (DB에도 매도 내역이 남도록)
    // - 규칙: 종료 버튼/50봉 종료/창 닫힘 등으로 게임이 끝나면, 마지막 봉 종가로 전량 매도 처리
    const tradeHistory = [...getTradeHistory()];
    if (finalStockQuantity > 0) {
      const tradeTime =
        lastCandle?.time ? new Date(lastCandle.time) : new Date();
      tradeHistory.push({
        type: 'sell',
        price: finalCurrentPrice,
        size: finalStockQuantity,
        time: tradeTime,
        reason: '게임 종료에 의한 전량 청산(강제 매도)',
      });
    }

    // 게임 종료 데이터를 먼저 설정하고 팝업을 즉시 표시
    const endData: GameEndData = {
      finalBalance,
      finalStockQuantity,
      finalCurrentPrice,
      finalCandleCount,
      finalTradeHistory: tradeHistory,
      startingBalance: initialBalanceBeforeReset,
      lastCandle, // 마지막 캔들 정보 포함
    };

    setGameEndData(endData);
    onGameEnd(endData);

    // 게임 상태를 즉시 비활성화하고 총자산을 보유현금에 반영
    setGameState(prev => ({
      ...prev,
      isGameActive: false,
      // 총자산을 즉시 보유현금에 반영 (주식은 모두 현금으로 환산)
      playerBalance: targetBalance,
      stockQuantity: 0,
      averagePrice: 0,
      // 지정가 주문 초기화
      limitOrders: [],
    }));

    // 서버 동기화는 백그라운드에서 처리 (팝업 표시를 막지 않음)
    (async () => {
      try {
        console.log('💰 잔고 저장 시작:', {
          user: !!user,
          targetBalance,
          gamesPlayedIncrement: 1,
        });
        const syncedResult = user
          ? await persistBalance(targetBalance, 1)
          : { balance: targetBalance, gameCount: 0 };
        const syncedBalance = syncedResult.balance;
        console.log('✅ 잔고 저장 성공:', {
          syncedBalance,
          gameCount: syncedResult.gameCount,
        });

        // 서버 동기화 완료 후 상태 업데이트 (잔고는 이미 위에서 업데이트됨)
        setGameState(prev => ({
          ...prev,
          currentPrice: 50000,
          history: [],
          playerBalance: syncedBalance, // 서버에서 동기화된 잔고로 업데이트
          playerPosition: null,
          positionSize: 0,
          entryPrice: null,
          profit: 0,
          stockQuantity: 0, // 이미 위에서 0으로 설정됨
          averagePrice: 0, // 이미 위에서 0으로 설정됨
          candleCount: 0,
          feedback: null,
          limitOrders: [], // 지정가 주문 초기화
          tradingStats: {
            totalTrades: 0,
            successfulTrades: 0,
            averageProfit: 0,
            bestTrade: 0,
            worstTrade: 0,
          },
          initialBalance: syncedBalance,
        }));
      } catch (error) {
        console.error('게임 종료 후 서버 동기화 실패:', error);
        // 에러가 발생해도 로컬 상태는 이미 업데이트됨 (위에서 처리)
        // 서버 동기화 실패 시에도 상태 정리는 진행
        setGameState(prev => ({
          ...prev,
          currentPrice: 50000,
          history: [],
          playerBalance: targetBalance, // 이미 위에서 설정됨
          playerPosition: null,
          positionSize: 0,
          entryPrice: null,
          profit: 0,
          stockQuantity: 0, // 이미 위에서 0으로 설정됨
          averagePrice: 0, // 이미 위에서 0으로 설정됨
          candleCount: 0,
          feedback: null,
          limitOrders: [], // 지정가 주문 초기화
          tradingStats: {
            totalTrades: 0,
            successfulTrades: 0,
            averageProfit: 0,
            bestTrade: 0,
            worstTrade: 0,
          },
          initialBalance: targetBalance,
        }));
      }
    })();

    setTimeLeft(300);
  }, [gameState, persistBalance, user, onGameEnd, getTradeHistory]);

  const startGameInternal = useCallback(
    (backgroundData: ChartData[], gameData: ChartData[]) => {
      // 원본 데이터를 그대로 사용 (과거 데이터는 당시의 호가 단위 규칙을 따름)
      setGameState(prev => ({
        ...prev,
        isGameActive: true,
        history: [...backgroundData, gameData[0]],
        currentPrice: gameData[0].close,
        candleCount: 0,
      }));
      setTimeLeft(300);
      setShowGameEndPopup(false);
      setGameEndData(null);
      onGameStart(backgroundData, gameData[0]);
    },
    [onGameStart]
  );

  const generateNextCandleInternal = useCallback(
    (
      getNextCachedData: () => ChartData | null,
      fetchAdditionalData: () => Promise<ChartData[] | null>,
      isAutoMode: boolean
    ): ChartData | null => {
      if (!gameState.isGameActive) return null;

      // 봉 개수 제한 체크 (추가 데이터 요청 전에 체크)
      const currentCandleCount = gameState.candleCount;
      if (currentCandleCount >= MAX_CANDLE_COUNT) {
        setTimeout(() => {
          endGame();
          setShowGameEndPopup(true);
        }, 100);
        return null;
      }

      let newCandle = getNextCachedData();

      if (!newCandle) {
        // 봉 개수 제한에 도달하면 추가 데이터 요청하지 않고 게임 종료
        if (currentCandleCount >= MAX_CANDLE_COUNT) {
          setTimeout(() => {
            endGame();
            setShowGameEndPopup(true);
          }, 100);
          return null;
        }
        // 추가 데이터 요청 (비동기이므로 즉시 반환하지 않음)
        fetchAdditionalData();
        // 서버 데이터가 없으면 랜덤 생성
        newCandle = generateNewCandle(
          gameState.currentPrice,
          gameState.history.length > 0
            ? gameState.history[gameState.history.length - 1].timestamp
            : Date.now() - 60000
        );
      }

      // newCandle이 null이 아니라는 것이 보장됨
      if (!newCandle) {
        return null;
      }

      const newCandleCount = gameState.candleCount + 1;

      // 봉 개수 제한 체크 (추가 안전 장치)
      if (newCandleCount > MAX_CANDLE_COUNT) {
        setTimeout(() => {
          endGame();
          setShowGameEndPopup(true);
        }, 100);
        return null;
      }

      // 수익 계산
      let profit = 0;
      if (gameState.playerPosition && gameState.entryPrice) {
        if (gameState.playerPosition === 'long') {
          profit =
            ((newCandle.close - gameState.entryPrice) / gameState.entryPrice) *
            100;
        } else {
          profit =
            ((gameState.entryPrice - newCandle.close) / gameState.entryPrice) *
            100;
        }
      }

      // 원본 데이터를 그대로 사용 (과거 데이터는 당시의 호가 단위 규칙을 따름)
      // 랜덤 생성된 캔들은 이미 호가 단위에 맞춰져 있음
      setGameState(prev => ({
        ...prev,
        currentPrice: newCandle!.close,
        history: [...prev.history, newCandle!],
        profit,
        candleCount: newCandleCount,
      }));

      // 50번째 봉이 생성된 직후 게임 종료 (자동 저장을 위해)
      if (newCandleCount === MAX_CANDLE_COUNT) {
        setTimeout(() => {
          endGame();
          setShowGameEndPopup(true);
        }, 100);
      }

      return newCandle;
    },
    [
      gameState.isGameActive,
      gameState.currentPrice,
      gameState.candleCount,
      gameState.history,
      gameState.playerPosition,
      gameState.entryPrice,
      endGame,
      setShowGameEndPopup,
    ]
  );

  return {
    gameState,
    setGameState,
    timeLeft,
    setTimeLeft,
    isAutoMode,
    setIsAutoMode,
    showGameEndPopup,
    setShowGameEndPopup,
    showExitConfirmPopup,
    setShowExitConfirmPopup,
    gameEndData,
    setGameEndData,
    resetGameState,
    endGame,
    startGameInternal,
    generateNextCandleInternal,
  };
};
